import { mat4, vec3 } from "gl-matrix";
import vtkDataArray from "vtk.js/Sources/Common/Core/DataArray";
import vtkImageData from "vtk.js/Sources/Common/DataModel/ImageData";
import vtkImageReslice from "vtk.js/Sources/Imaging/Core/ImageReslice";
import { crossVectors } from "./math";
import {
  mapVtkSliceToOurImage,
  insertSlice,
  getSliceIndex,
  getTypedPixelArray,
  computeZAxis,
  determineOrientation,
} from "./utils/";
import Cache from "core/cache/index";

/**
 * Creates a 4x4 matrix that vtk can use as a "rotation matrix". The values
 * correspond to:
 *
 * ux, uy, uz, 0
 * vx, vy, vz, 0
 * wx, wy, wz, 0
 * px, py, pz, 1
 *
 * ux, uy, uz, vx, vy, vz - "ImageOrientationPatient"
 * w - cross_product(u,v)
 * px, py, pz - "ImagePositionPatient"
 *
 * ImagePositionPatient: [60.3642578125, 170.3642578125, -32]
 * ImageOrientationPatient: [-1, 0, 0, 0, -1, 0]
 * RowCosines: [-1, 0, 0]
 * ColumnCosines: [0, -1, 0]
 *
 * Reference: https://public.kitware.com/pipermail/vtkusers/2012-November/077297.html
 * Reference: http://nipy.org/nibabel/dicom/dicom_orientation.html
 *
 * @param {Float32Array} rowCosines
 * @param {Float32Array} colCosines
 * @param {Float32Array} ippArray
 * @returns {Mat4} - 4x4 Rotation Matrix
 */
function _calculateRotationAxes(rowCosines, colCosines, ippArray) {
  let wCrossProd = vec3.create();
  vec3.cross(wCrossProd, rowCosines, colCosines);

  const axes = mat4.fromValues(
    rowCosines[0],
    rowCosines[1],
    rowCosines[2],
    0,
    colCosines[0],
    colCosines[1],
    colCosines[2],
    0,
    wCrossProd[0],
    wCrossProd[1],
    wCrossProd[2],
    0,
    ippArray[0],
    ippArray[1],
    ippArray[2],
    1
  );

  return axes;
}

/**
 * Calculates the center IPP for the volume. Useful for displaying
 * "best" slice on first render.
 *
 * @param {*} vtkImageData
 * @returns {Vec3} - Float32Array contain the volume's center IPP
 */
function _getVolumeCenterIpp(vtkImageData) {
  const [x0, y0, z0] = vtkImageData.getOrigin();
  const [xSpacing, ySpacing, zSpacing] = vtkImageData.getSpacing();
  const [xMin, xMax, yMin, yMax, zMin, zMax] = vtkImageData.getExtent();

  const centerOfVolume = vec3.fromValues(
    x0 + xSpacing * 0.5 * (xMin + xMax),
    y0 + ySpacing * 0.5 * (yMin + yMax),
    z0 + zSpacing * 0.5 * (zMin + zMax)
  );

  return centerOfVolume;
}

function _getDimensions(dataID) {
  let metadataCache = new Cache.Metadata();
  const rowCosines = metadataCache.getRowCosines(dataID);
  const columnCosines = metadataCache.getColumnCosines(dataID);
  const crossProduct = crossVectors(columnCosines, rowCosines);
  const orientation = determineOrientation(crossProduct);
  const frames = metadataCache.get(dataID);
  const zAxis = computeZAxis(orientation, frames);

  const [ySpacing, xSpacing] = metadataCache.getPixelSpacing(dataID, 0);
  const zSpacing = metadataCache.getThickness(dataID);
  //const zSpacing = 0.5;
  const xVoxels = metadataCache.getColumns(dataID, 0);
  const yVoxels = metadataCache.getRows(dataID, 0);
  const zVoxels = metadataCache.getNumOfFrame(dataID);

  // is RGB, multiComponent
  // but, not supported. only grayscale
  const multiComponent = false;
  return {
    dimensions: [xVoxels, yVoxels, zVoxels],
    orientation,
    multiComponent,
    spacing: [xSpacing, ySpacing, zSpacing],
    zAxis,
  };
}

function _createVtkVolumeByiAIDModel(seriesData, dimensions, spacing, zAxis) {
  const vtkVolume = vtkImageData.newInstance();
  const firstFrame = seriesData.stack[0].frame[0];
  const pixelRepresentation = firstFrame.pixelRepresentation;
  const bitsAllocated = firstFrame.bitsAllocated;

  const typedPixelArray = getTypedPixelArray(
    bitsAllocated,
    pixelRepresentation,
    dimensions
  );
  const scalarArray = vtkDataArray.newInstance({
    name: "Pixels",
    numberOfComponents: 1,
    values: typedPixelArray,
  });
  // TODO: Is this a better place to set this?
  //console.log("VOLUME ORIGIN: ", zAxis.origin);
  // Our recommended three-step method for
  // handling DICOM coordinates in VTK are to undo any vertical flip applied to the data by the reader, set the
  // origin to zero, and build the above 4×4 matrix from the DICOM metadata.
  // vtkVolume.setOrigin(zAxis.origin)
  //vtkVolume.setOrigin([0, 0, 0]); // zMax * zPixelSpacing
  vtkVolume.setOrigin(zAxis.origin); // zMax * zPixelSpacing
  vtkVolume.setDimensions(dimensions);
  vtkVolume.setSpacing(spacing);
  vtkVolume.getPointData().setScalars(scalarArray);

  // Add our slices

  const frames = seriesData.stack[0].frame;

  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    const imagePosition = frames[i].imagePosition;
    const sliceIndex = getSliceIndex(zAxis, imagePosition);

    insertSlice(vtkVolume, frame.pixelData, sliceIndex);

    // TODO: Inverting Slice Index: Vertical flips sagittal/coronal
    // const flipped = Math.abs(sliceIndex - seriesImageIds.length);
    // insertSlice(vtkVolume, image.getPixelData().reverse(), flipped);

    // TODO: .reverse() vertically flips axial
    // TODO: Flip x/y spacing because of flip?
    // insertSlice(vtkVolume, image.getPixelData().reverse(), sliceIndex);
  }

  // TODO: We can accidentally create multiple volumes if we try to create one
  // Before a request for the same series has completed.
  // (You'll notice this logs 3x -- one for each initial MPR canvas, but 0x after any load has finished)
  // console.log('~~~~~~~~~~ VTK VOLUME:', vtkVolume);
  return vtkVolume;
}

//function createMPRImage(image, dataID, options) {
function createMPRImage(image, options) {
  let seriesInstanceKey = image.seriesInstanceUID || "NoneSeriesUID";
  let modalityKey = image.modality || "NoneModality";

  const dataID = seriesInstanceKey + "_" + modalityKey;

  // get Dimensions
  const {
    dimensions,
    orientation,
    multiComponent,
    spacing,
    zAxis,
  } = _getDimensions(dataID);
  let metadataCache = new Cache.Metadata();
  const frames = metadataCache.get(dataID);
  const firstFrame = frames.stack[0].frame[0];
  var before = performance.now();
  let vtkImageData = metadataCache.getMPRData(dataID);
  if (!!vtkImageData === false) {
    vtkImageData = _createVtkVolumeByiAIDModel(
      frames,
      dimensions,
      spacing,
      zAxis
    );
    metadataCache.addMPRData(dataID, vtkImageData);
  }
  var after = performance.now();
  //console.log("1. " + (after - before) + " ms.");
  const iop = options.imageOrientation || [1, 0, 0, 0, 1, 0];
  //const ipp = [0, 0, 0];
  const ipp = options.imagePosition || [0, 0, 0];

  const rowCosinesVec3 = vec3.fromValues(iop[0], iop[1], iop[2]);
  const colCosinesVec3 = vec3.fromValues(iop[3], iop[4], iop[5]);
  const ippVec3 =
    ipp === "center"
      ? _getVolumeCenterIpp(vtkImageData)
      : vec3.fromValues(ipp[0], ipp[1], ipp[2]);

  const zedCosinesVec3 = vec3.create(); // (0, 0, 0)
  vec3.cross(zedCosinesVec3, rowCosinesVec3, colCosinesVec3);
  const [x0, y0, z0] = vtkImageData.getOrigin();
  //const [x0, y0, z0] = [0, 0, 0];
  /**
   * x/y pixel spacing = pixel spacing (0028, 0030) tag
   * z pixel spacing  = slice thickness (0018, 0050) tag
   */
  const zSpacing = image.sliceThickness;

  // let a = "adfsdf";
  // a = "df";

  /**
   * x/y/z min-max = spatial min/max
   */
  const zMin = 0;
  const zMax = metadataCache.getNumOfFrame(dataID);
  const zStart = z0 + zSpacing * (zMax - zMin);

  const position = vec3.fromValues(
    zedCosinesVec3[0] * -1 * (ippVec3[0] - x0) + x0,
    zedCosinesVec3[1] * (ippVec3[1] - y0) + y0,
    zedCosinesVec3[2] * (ippVec3[2] - zStart) + zStart
  );
  const axes = _calculateRotationAxes(rowCosinesVec3, colCosinesVec3, position);
  // Setup vtkImageReslice
  const imageReslice = vtkImageReslice.newInstance();
  imageReslice.setInputData(vtkImageData); // Our volume
  imageReslice.setOutputDimensionality(2); // We want a "slice", not a volume
  imageReslice.setBackgroundColor(255, 255, 255, 255); // Black background
  imageReslice.setResliceAxes(axes); // Rotational Axes
  const sopInstanceUID = axes.toString();
  // Pull the lever!
  const outputSlice = imageReslice.getOutputData();
  const mappedSlice = mapVtkSliceToOurImage(outputSlice);

  const outputImage = {
    color: false,
    sopInstanceUID: sopInstanceUID,
    seriesInstanceUID: image.seriesInstanceUID,
    rowCosines: [axes[0], axes[1], axes[2]],
    frameOfReferenceUID: image.frameOfReferenceUID,
    columnCosines: [axes[4], axes[5], axes[6]],
    columnPixelSpacing: mappedSlice.columnPixelSpacing,
    rowPixelSpacing: mappedSlice.rowPixelSpacing,
    pixelSpacing: [
      mappedSlice.rowPixelSpacing,
      mappedSlice.columnPixelSpacing,
      zSpacing || 1,
    ],
    columns: mappedSlice.columns,
    width: mappedSlice.width,
    height: mappedSlice.height,
    rows: mappedSlice.rows,
    imageOrientationPatient: [
      axes[0],
      axes[1],
      axes[2],
      axes[4],
      axes[5],
      axes[6],
    ],
    imagePosition: [axes[12], axes[13], axes[14]],
    rescaleIntercept: firstFrame.rescaleIntercept,
    rescaleSlope: firstFrame.rescaleSlope,
    intercept: 0,
    invert: false,
    modality: image.modality,
    pixelData: mappedSlice.pixelData,
    getPixelData: () => mappedSlice.pixelData,
    minPixelValue: mappedSlice.minPixelValue,
    maxPixelValue: mappedSlice.maxPixelValue,
    minMax: [mappedSlice.minPixelValue, mappedSlice.maxPixelValue],
    sizeInBytes: mappedSlice.sizeInBytes,
    slope: 1,
    windowCenter: firstFrame.windowCenter || mappedSlice.windowCenter,
    windowWidth: firstFrame.windowWidth || mappedSlice.windowWidth,
    decodeTimeInMS: 0,
    floatPixelData: undefined,
    isMpr: true,
  };

  return outputImage;
}

export default createMPRImage;
